import React, { useState, useEffect } from "react";
import { Building2, Plus, Search, MapPin, Phone, Trash2, X, PlusCircle, LayoutGrid, Menu, CheckCircle2, ShieldX } from "lucide-react";
import AdminSidebar from "./AdminSidebar";
import { db } from "../../firebase/config";
import { useToast } from "../../context/ToastContext";
import { ref, onValue, set, remove, update } from "firebase/database";
import { generateClinicalId } from "../../utils/idGenerator";
import "./ManageHospitals.css";

const ManageHospitals = () => {
  const { showAlert } = useToast();
  const [hospitals, setHospitals] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [newHospital, setNewHospital] = useState({
    name: "",
    location: "",
    contact: "",
    capacity: 100,
    status: "active"
  });

  useEffect(() => {
    const hospitalsRef = ref(db, "hospitals");
    const unsubscribe = onValue(hospitalsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const list = Object.entries(data).map(([id, val]) => ({ id, ...val }));
        setHospitals(list);
      } else {
        setHospitals([]);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleAddHospital = async (e) => {
    e.preventDefault();
    try {
      const hspId = await generateClinicalId("hospital");
      const hospitalRef = ref(db, `hospitals/${hspId}`);
      await set(hospitalRef, {
        ...newHospital,
        id: hspId,
        createdAt: new Date().toISOString()
      });
      setIsModalOpen(false);
      setNewHospital({ name: "", location: "", contact: "", capacity: 100, status: "active" });
      showAlert(`FACILITY_INITIALIZED: Identity node ${hspId} successfully onboarded.`, "success");
    } catch (error) {
      showAlert("ONBOARDING_FAILURE: " + error.message, "error");
    }
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      await update(ref(db, `hospitals/${id}`), { status });
      showAlert(`PROTOCOL_UPDATED: Facility node status changed to ${status.toUpperCase()}.`, "info");
    } catch (error) {
      showAlert("STATUS_SYNC_FAILURE: " + error.message, "error");
    }
  };

  const handleDeleteHospital = async (id) => {
    if (window.confirm("CONFIRM_ACTION: De-authorize this clinical facility node?")) {
      try {
        await remove(ref(db, `hospitals/${id}`));
        showAlert("NODE_DE_AUTHORIZED: Facility purged from network.", "success");
      } catch (error) {
        showAlert("PROTOCOL_ERROR: " + error.message, "error");
      }
    }
  };

  const filteredHospitals = hospitals.filter(h => 
    (h.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
    (h.location || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="admin-shell">
      <AdminSidebar />
      
      <main className="admin-content">
        <header className="admin-header">
          <div className="header-info">
            <h1>PARTNER_NETWORK</h1>
            <p>OVERSIGHT_NODE: GLOBAL_FACILITIES_v1.0</p>
          </div>
          <button className="premium-btn primary" onClick={() => setIsModalOpen(true)}>
            <PlusCircle size={18} strokeWidth={2.5} />
            ONBOARD_NEW_FACILITY
          </button>
        </header>

        <section className="registry-controls glass-card">
          <div className="search-group">
            <Search size={18} className="icon-blue" strokeWidth={2.5} />
            <input 
              type="text" 
              placeholder="SEARCH_BY: FACILITY_NAME OR_LOCATION_ID..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="registry-summary">
            ACTIVE_NODES: [ {hospitals.length.toString().padStart(3, '0')} ]
          </div>
        </section>

        <div className="hospitals-grid">
          {filteredHospitals.length > 0 ? (
            filteredHospitals.map((hosp) => (
              <div key={hosp.id} className="hospital-card glass-card animate-scale-up">
                <div className={`status-badge ${hosp.status}`}>
                  {hosp.status.toUpperCase()}
                </div>
                <div className="hospital-card-header">
                  <div className="hospital-icon-box">
                    <Building2 size={24} className="icon-blue" strokeWidth={2.5} />
                  </div>
                  <div className="hospital-title-box">
                    <h3>{hosp.name}</h3>
                    <span>NODE_ID: {hosp.id.slice(-8).toUpperCase()}</span>
                  </div>
                </div>

                <div className="hospital-body" style={{ marginTop: '2rem', gap: '1.25rem' }}>
                  <div className="info-item">
                    <LayoutGrid size={14} className="icon-blue" />
                    <span>TOTAL_CAPACITY: {(hosp.capacity || 0).toString().padStart(3, '0')}</span>
                  </div>
                </div>

                <div className="hospital-footer">
                  <div className="footer-actions-main">
                    {hosp.status === "pending" ? (
                      <>
                        <button 
                          className="hosp-action-btn view" 
                          onClick={() => handleStatusUpdate(hosp.id, "active")}
                        >
                          <CheckCircle2 size={14} /> AUTHORIZE_FACILITY
                        </button>
                        <button 
                          className="hosp-action-btn delete" 
                          onClick={() => handleStatusUpdate(hosp.id, "rejected")}
                        >
                          <ShieldX size={14} /> DE_AUTHORIZE
                        </button>
                      </>
                    ) : (
                      <button className="hosp-action-btn view">
                        VIEW_ANALYTICS
                      </button>
                    )}
                  </div>
                  <button className="hosp-action-btn delete" onClick={() => handleDeleteHospital(hosp.id)}>
                    <Trash2 size={18} strokeWidth={2.5} />
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="empty-hospitals glass-card">
              <Building2 size={64} className="icon-dim pulse" />
              <p>
                NO_CLINICAL_FACILITIES_FOUND_ON_NETWORK
              </p>
            </div>
          )}
        </div>

        {/* Onboarding HUD Modal */}
        {isModalOpen && (
          <div className="modal-backdrop">
            <div className="admin-modal-card glass-card animate-slide-up">
              <div className="modal-header">
                <div className="modal-title-box">
                  <Building2 size={24} className="icon-blue" strokeWidth={2.5} />
                  <h2 style={{ fontSize: '1.5rem', fontWeight: 950, letterSpacing: '-1px' }}>FACILITY_ONBOARDING</h2>
                </div>
                <button className="close-btn" onClick={() => setIsModalOpen(false)}>
                  <X size={20} strokeWidth={2.5} />
                </button>
              </div>

              <form onSubmit={handleAddHospital} className="modal-body" style={{ marginTop: '2.5rem' }}>
                <div className="input-group">
                  <label>LEGAL_ENTITY_NAME</label>
                  <input 
                    type="text" 
                    placeholder="E.G., GLOBAL_HEALTH_PLAZA" 
                    value={newHospital.name}
                    onChange={(e) => setNewHospital({...newHospital, name: e.target.value})}
                    required
                  />
                </div>
                <div className="input-group">
                  <label>GEOGRAPHIC_POSITION</label>
                  <input 
                    type="text" 
                    placeholder="E.G., SECTOR_45, TECH_CITY" 
                    value={newHospital.location}
                    onChange={(e) => setNewHospital({...newHospital, location: e.target.value})}
                    required
                  />
                </div>
                <div className="modal-form-row">
                  <div className="input-group">
                    <label>TELEMETRY_PHONE</label>
                    <input 
                      type="text" 
                      placeholder="+91 11 2233 4455" 
                      value={newHospital.contact}
                      onChange={(e) => setNewHospital({...newHospital, contact: e.target.value})}
                      required
                    />
                  </div>
                  <div className="input-group">
                    <label>BED_CAPACITY_MAX</label>
                    <input 
                      type="number" 
                      placeholder="500" 
                      value={newHospital.capacity}
                      onChange={(e) => setNewHospital({...newHospital, capacity: parseInt(e.target.value)})}
                      required
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="cancel-link" onClick={() => setIsModalOpen(false)}>
                    ABORT_PROTOCOL
                  </button>
                  <button type="submit" className="premium-btn primary">
                    INITIALIZE_ONBOARDING
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ManageHospitals;
